'use client'

/**
 * Scroll restoration for SPA-style navigations backed by useSWRInfinite.
 *
 * Why module-level helpers instead of hook-internal state (à la usehooks-ts)?
 * — Scroll state must outlive the component (save on unmount → restore on
 *   remount), so we use sessionStorage directly instead of useState-backed
 *   wrappers that re-render on every write and die with the component tree.
 * — `onPopState` must be a stable reference so addEventListener deduplicates
 *   across remounts without useCallback + manual cleanup.
 * — Pure functions at module scope cost nothing per render and are trivially
 *   testable without mounting a component.
 *
 * Calling convention (order matters):
 *   1. useSavedPages()           — captures isBackNav + savedPages at mount
 *   2. useSWRInfinite(...)       — normal SWR call
 *   3. useScrollRestorationSWR() — wires restore; returns tracked setSize
 *
 * ⚠️  Non-obvious invariants — read before refactoring:
 *
 * - `onPopState` has NO cleanup in useEffect. This is intentional:
 *   addEventListener deduplicates by function reference, and the BACK_KEY
 *   flag must survive component unmount → remount on back-nav.
 *   DO NOT add a removeEventListener cleanup.
 *
 * - `restored` ref in useScrollRestore intentionally does NOT reset when
 *   the route key changes. If the component stays mounted across route
 *   changes (e.g. genre filter), re-restoring scroll would be wrong.
 *   DO NOT add `restored.current = false` on key change.
 *
 * - Phase 2 effect intentionally does NOT set `restored = true` when the
 *   anchor card is missing. It leaves the ref false so the effect retries
 *   on the next `settled` transition (handles deferred SWR keys).
 *   DO NOT add a fallback `restored.current = true` at the end.
 *
 * - useScrollRestoration saves position in a useIsomorphicLayoutEffect
 *   cleanup (not useEffect cleanup) because layout cleanup runs before
 *   the DOM detaches. useEffect cleanup fires too late — scrollTop is 0.
 *   DO NOT convert to useEffect.
 *
 * - BACK_KEY is cleared at the TOP of the layout effect, not in a separate
 *   effect. This ensures the flag is consumed exactly once per mount.
 *   DO NOT move the clear to a separate useEffect.
 *
 * - useActivityScrollFallback must stay a LAYOUT effect. Activity runs effect
 *   cleanup before it applies display:none, which is the last moment the
 *   container's scrollTop is still readable. DO NOT convert to useEffect.
 */

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'

// --- SessionStorage --------------------------------------------------------
//
// Safe wrappers for SSR (no window) and disabled/quota scenarios.
// Module-level so reads/writes work outside the React tree (event handlers,
// layout-effect cleanup, etc.).

type ScrollEntry = { pos?: number; anchor?: string; pages?: number }

/** Prefix for per-route scroll entries in sessionStorage. */
const scrollKey = (route: string) => `scroll:${route}`

/** SessionStorage flag: set to '1' by onPopState before React re-renders. */
const BACK_KEY = 'scroll:back'

const storage = {
  get(key: string): string | null {
    if (typeof window === 'undefined') return null
    try {
      return sessionStorage.getItem(key)
    } catch {
      return null
    }
  },
  set(key: string, value: string): void {
    if (typeof window === 'undefined') return
    try {
      sessionStorage.setItem(key, value)
    } catch {
      /* quota exceeded or storage disabled — safe to ignore */
    }
  },
}

function readEntry(route: string): ScrollEntry {
  try {
    return JSON.parse(storage.get(scrollKey(route)) ?? '{}')
  } catch {
    return {}
  }
}

function writeEntry(route: string, patch: ScrollEntry) {
  storage.set(scrollKey(route), JSON.stringify({ ...readEntry(route), ...patch }))
}

// --- DOM -------------------------------------------------------------------

const SCROLL_CONTAINER_SELECTOR = '[data-scroll-container]'
const ROOM_CARD_SELECTOR = '[data-room-id]'

function getScrollContainer() {
  return document.querySelector<HTMLElement>(SCROLL_CONTAINER_SELECTOR)
}

/**
 * Find the room card at the visual center of the scroll viewport — the
 * "anchor" card the user was looking at before navigating away.
 *
 * Uses getBoundingClientRect (viewport coords) so it works regardless of
 * offsetParent — some routes wrap cards in a positioned div.
 */
function findAnchorCard(scrollEl: HTMLElement): HTMLElement | undefined {
  const viewportCenter = scrollEl.getBoundingClientRect().top + scrollEl.clientHeight / 2
  return Array.from(scrollEl.querySelectorAll<HTMLElement>(ROOM_CARD_SELECTOR)).find(
    (card) => card.getBoundingClientRect().bottom > viewportCenter,
  )
}

function findRoomCard(scrollEl: HTMLElement, roomId: string): HTMLElement | null {
  return scrollEl.querySelector<HTMLElement>(`[data-room-id="${CSS.escape(roomId)}"]`)
}

/** Instantly scroll the anchor card to center, bypassing smooth-scroll and overflow-anchor. */
function snapToCard(scrollEl: HTMLElement, card: HTMLElement) {
  scrollEl.style.scrollBehavior = 'auto'
  scrollEl.style.overflowAnchor = 'none'
  card.scrollIntoView({ block: 'center', behavior: 'instant' })
  scrollEl.style.scrollBehavior = ''
  scrollEl.style.overflowAnchor = ''
}

// --- Navigation helpers ----------------------------------------------------

/** Build a storage key from pathname + search params. */
function routeKey(path: string, query: string) {
  return query ? `${path}?${query}` : path
}

function getSavedPageCount(route: string) {
  return readEntry(route).pages ?? 1
}

/** Read the navigation type from the Performance API (e.g. 'back_forward', 'reload'). */
function getNavigationType() {
  return (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
    ?.type
}

/**
 * WebKit: desktop Safari plus every iOS browser (iOS Chrome is `CriOS`, so it
 * carries no `Chrome/` token and must match). Chromium also ships `AppleWebKit`,
 * hence the exclusion. Remove with useActivityScrollFallback once WebKit
 * preserves a hidden container's scrollTop.
 */
const IS_WEBKIT =
  typeof navigator !== 'undefined' &&
  /AppleWebKit/.test(navigator.userAgent) &&
  !/Chrome\//.test(navigator.userAgent)

/**
 * Module-level popstate handler. Must be a named function (not a per-render
 * closure) so addEventListener deduplicates by reference across remounts.
 * Sets a sessionStorage flag *before* React re-renders on back-nav.
 */
function onPopState() {
  storage.set(BACK_KEY, '1')
}

// --- Internal hooks --------------------------------------------------------

function useRouteKey() {
  const pathname = usePathname()
  const search = useSearchParams().toString()
  return routeKey(pathname, search)
}

/**
 * Two-phase scroll restore — tries to land on the exact card the user saw:
 *
 * Phase 1 (layout effect, before paint):
 *   If the anchor card is already in the DOM (SSR'd swimlane), snap to it.
 *   Otherwise rough-scroll to saved pixel position to avoid flashing the top.
 *
 * Phase 2 (effect, after SWR settles):
 *   Now that data loaded, find the anchor card and snap. Retries on each
 *   settled transition (false→true) to handle deferred keys (browserLocale,
 *   htcToken).
 */
function useScrollRestore(isBackNav: boolean, settled: boolean): void {
  const route = useRouteKey()
  // ⚠️ INVARIANT: Do NOT reset on `route` change — see module doc.
  // If the component stays mounted across route changes (e.g. genre filter
  // in ExploreViverse), re-restoring scroll would be wrong.
  const restored = useRef(false)

  // Phase 1 — before paint
  useIsomorphicLayoutEffect(() => {
    if (restored.current || !isBackNav) return
    const scrollEl = getScrollContainer()
    if (!scrollEl) return

    const { anchor: roomId = '', pos = 0 } = readEntry(route)
    if (!roomId) {
      restored.current = true
      return
    }

    const card = findRoomCard(scrollEl, roomId)
    if (card) {
      // Best-effort rough snap before paint. Do NOT mark restored — Phase 2
      // owns the final snap after SWR settles and the recommendWorlds prepend
      // resolves, which can shift the anchor after this snap.
      snapToCard(scrollEl, card)
      return
    }

    // Card not in DOM yet — rough-scroll so the user doesn't see top-of-page
    if (pos > 0) {
      scrollEl.style.scrollBehavior = 'auto'
      scrollEl.scrollTo({ top: pos, behavior: 'instant' })
      scrollEl.style.scrollBehavior = ''
    }
  }, [isBackNav, route])

  // Phase 2 — after SWR settles
  // ⚠️ INVARIANT: when the card is missing, we intentionally leave
  // `restored` as false so the effect retries — see module doc.
  useEffect(() => {
    if (restored.current || !isBackNav || !settled) return
    const scrollEl = getScrollContainer()
    if (!scrollEl) return

    const { anchor: roomId = '' } = readEntry(route)
    if (!roomId) {
      restored.current = true
      return
    }

    const card = findRoomCard(scrollEl, roomId)
    if (!card) return
    restored.current = true
    snapToCard(scrollEl, card)
  }, [isBackNav, settled, route])
}

/** Bumps SWR page size if initialSize was missed (e.g. Activity re-show, late mount). */
function useResumeSWR(
  route: string,
  isBackNav: boolean,
  size: number,
  setSize: (n: number) => void,
) {
  const resumed = useRef(false)
  useEffect(() => {
    if (resumed.current || !isBackNav) return
    resumed.current = true
    const saved = getSavedPageCount(route)
    if (saved > size) setSize(saved)
  }, [isBackNav, route, size, setSize])
}

// --- Public API ------------------------------------------------------------

/**
 * Snapshot back-nav state and saved page count at mount.
 * Call *before* useSWRInfinite so the snapshot is taken before SWR mutates size.
 *
 * Uses useState initializers (run once on mount) to freeze the values —
 * equivalent to a ref but without the ref-during-render footgun.
 */
export function useSavedPages() {
  const route = useRouteKey()
  const [isBackNav] = useState(
    () => storage.get(BACK_KEY) === '1' || getNavigationType() === 'back_forward',
  )
  const [savedPages] = useState(() => (isBackNav ? getSavedPageCount(route) : 1))
  return { isBackNav, savedPages } as const
}

type SWRState = {
  size: number
  setSize: (n: number) => void
  isLoading: boolean
  isValidating: boolean
}

/**
 * Wire scroll restoration into a useSWRInfinite result.
 * Call *after* useSWRInfinite, passing its return values.
 * Returns a tracked `setSize` that persists page count to sessionStorage
 * so back-nav can restore the correct number of pages.
 */
export function useScrollRestorationSWR(saved: ReturnType<typeof useSavedPages>, swr: SWRState) {
  const route = useRouteKey()

  useResumeSWR(route, saved.isBackNav, swr.size, swr.setSize)
  useScrollRestore(saved.isBackNav, !swr.isLoading && !swr.isValidating)

  return (n: number) => {
    swr.setSize(n)
    writeEntry(route, { pages: n })
  }
}

/**
 * Derive skeleton visibility and count from SWR + restore state.
 * Pure function — no hooks, safe to call conditionally.
 */
export function skeletonState(
  saved: Pick<ReturnType<typeof useSavedPages>, 'isBackNav' | 'savedPages'>,
  swr: Pick<SWRState, 'isLoading' | 'isValidating'>,
  loadedCount: number,
  limit: number,
  loadingCount = limit,
) {
  const target = saved.savedPages * limit
  const restoreCount =
    saved.isBackNav && loadedCount < target ? Math.max(0, target + limit - loadedCount) : 0
  return {
    show: swr.isLoading || swr.isValidating || restoreCount > 0,
    count: restoreCount > 0 ? restoreCount : loadingCount,
  }
}

/**
 * Skeleton count for loading.tsx / Suspense boundaries.
 * Reads sessionStorage at mount to match the skeleton count to the saved
 * page count — avoids layout shift on back-nav.
 */
export function useFallbackSkeletons(limit: number, ssrFallback = limit) {
  const [count] = useState(() => {
    if (typeof window === 'undefined') return ssrFallback

    const route = routeKey(
      window.location.pathname,
      new URLSearchParams(window.location.search).toString(),
    )

    return getSavedPageCount(route) * limit + limit
  })

  return count
}

/**
 * WebKit loses a hidden container's scrollTop across Next's Activity-based route
 * preservation (`cacheComponents`); Chromium keeps it (CONNECT-5985). Re-applies
 * the position, then hands off to the anchor snap so soft-nav lands as precisely
 * as the back-nav path.
 *
 * Restores only on a return to the SAME history entry AND the same list:
 *   - same `bfcacheId` — a new entry (push/replace) must land at top.
 *   - same `route`     — `routeKey` includes the query string, so a query-only
 *     change (e.g. a genre filter) is a different list and must not restore.
 *
 * Deliberately does NOT gate on BACK_KEY. Activity re-shows the subtree and runs
 * this effect BEFORE the DOM popstate fires, so the flag is still '' here and the
 * restore would never run — the original CONNECT-5985 race. Measured, not guessed.
 */
function useActivityScrollFallback(route: string): void {
  const { bfcacheId } = useRouter()
  const hiddenPos = useRef(0)
  const hiddenId = useRef<string | null>(null)
  const hiddenRoute = useRef<string | null>(null)

  useIsomorphicLayoutEffect(() => {
    if (!IS_WEBKIT) return

    const isSameEntry = hiddenId.current === bfcacheId && hiddenRoute.current === route

    const scrollEl = getScrollContainer()
    if (scrollEl && isSameEntry && hiddenPos.current > 0) {
      scrollEl.style.scrollBehavior = 'auto'
      scrollEl.scrollTo({ top: hiddenPos.current, behavior: 'instant' })
      scrollEl.style.scrollBehavior = ''

      const { anchor = '' } = readEntry(route)
      const card = anchor ? findRoomCard(scrollEl, anchor) : null
      if (card) snapToCard(scrollEl, card)
    }

    return () => {
      const el = getScrollContainer()
      if (!el) return
      hiddenPos.current = el.scrollTop
      hiddenId.current = bfcacheId
      hiddenRoute.current = route
    }
  }, [bfcacheId, route])
}

/**
 * Layout-level singleton — call once in the scroll container's parent.
 * Saves scroll position + anchor card on every navigation away.
 *
 * - `onPopState` is a module-level stable ref so addEventListener dedupes
 *   by reference. No cleanup: the flag must survive unmount/remount.
 * - Layout-effect cleanup saves position *before* the DOM detaches
 *   (useEffect cleanup fires too late). `pagehide` covers tab-close /
 *   tab-background where React unmount never fires.
 */
export function useScrollRestoration(): void {
  const route = useRouteKey()

  useActivityScrollFallback(route)

  // ⚠️ INVARIANT: no removeEventListener cleanup — see module doc.
  // onPopState is module-level; addEventListener dedupes by reference.
  useEffect(() => {
    history.scrollRestoration = 'manual'
    window.addEventListener('popstate', onPopState)
  }, [])

  // ⚠️ INVARIANT: must be layout effect, not useEffect — see module doc.
  // useEffect cleanup fires after DOM detach; scrollTop would be 0.
  useIsomorphicLayoutEffect(() => {
    // Detect and consume the back-nav flag. This layout effect fires on every
    // route change (via [route] dep), so it's the earliest reliable point to
    // snap SSR'd cards (swimlanes) already in the DOM. Async client lists
    // (ExploreViverse) self-restore later via their own SWR settle phase.
    const isBackNav = storage.get(BACK_KEY) === '1' || getNavigationType() === 'back_forward'
    storage.set(BACK_KEY, '')

    const scrollEl = getScrollContainer()

    // Phase 1 restore — snap to the anchor card if it's already in the DOM.
    if (isBackNav && scrollEl) {
      const { anchor: roomId = '', pos = 0 } = readEntry(route)
      if (roomId) {
        const card = findRoomCard(scrollEl, roomId)
        if (card) {
          snapToCard(scrollEl, card)
        } else if (pos > 0) {
          scrollEl.style.scrollBehavior = 'auto'
          scrollEl.scrollTo({ top: pos, behavior: 'instant' })
          scrollEl.style.scrollBehavior = ''
        }
      }
    }

    const savePosition = () => {
      if (!scrollEl || scrollEl.scrollTop === 0) return
      writeEntry(route, {
        pos: scrollEl.scrollTop,
        anchor: findAnchorCard(scrollEl)?.dataset.roomId ?? '',
      })
    }

    window.addEventListener('pagehide', savePosition)
    return () => {
      window.removeEventListener('pagehide', savePosition)
      savePosition()
    }
  }, [route])
}
